import { z } from 'zod';

// User roles matching Cognito groups
export const UserRole = z.enum([
  'admin',
  'finance', 
  'buyer',
  'supervisor',
  'site-manager',
  'storekeeper',
  'receiving',
  'chef',
  'venue-staff',
  'supplier-admin',
  'supplier-sales',
  'supplier-delivery',
]);

export type UserRole = z.infer<typeof UserRole>;

// User status based on Cognito user state
// active: CONFIRMED and enabled
// pending: FORCE_CHANGE_PASSWORD (invited but not logged in)
// disabled: CONFIRMED but disabled
// unconfirmed: UNCONFIRMED
export const UserStatus = z.enum([
  'active',
  'pending',
  'disabled',
  'unconfirmed',
]);

export type UserStatus = z.infer<typeof UserStatus>;

// User schema matching Lambda response
export const userSchema = z.object({
  userId: z.string(),
  email: z.string().email(),
  username: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  organizationId: z.string().optional(),
  role: UserRole,
  siteAccess: z.array(z.string()),
  venueAccess: z.array(z.string()),
  defaultSiteId: z.string().optional(),
  supplierId: z.string().optional(),
  preferredLanguage: z.string().optional(),
  status: UserStatus,
  emailVerified: z.boolean().optional(),
  mfaEnabled: z.boolean().optional(),
  lastLoginAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type User = z.infer<typeof userSchema>;

export const usersResponseSchema = z.object({
  data: z.array(userSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number(),
  }),
});

export type UsersResponse = z.infer<typeof usersResponseSchema>;

export const userResponseSchema = z.object({
  data: userSchema,
  message: z.string().optional(),
});

export type UserResponse = z.infer<typeof userResponseSchema>;

// Invite user request
export const inviteUserRequestSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  role: UserRole,
  siteAccess: z.array(z.string()).default([]),
  venueAccess: z.array(z.string()).default([]),
  defaultSiteId: z.string().optional(),
  supplierId: z.string().optional(),
  preferredLanguage: z.string().default('en'),
});

export type InviteUserRequest = z.infer<typeof inviteUserRequestSchema>;

// Update user request
export const updateUserRequestSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  role: UserRole.optional(),
  siteAccess: z.array(z.string()).optional(),
  venueAccess: z.array(z.string()).optional(),
  defaultSiteId: z.string().optional(),
  supplierId: z.string().optional(),
  preferredLanguage: z.string().optional(),
});

export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

// User action types
export const UserAction = z.enum([
  'disable',
  'enable', 
  'reset-password',
  'resend-invitation',
]);

export type UserAction = z.infer<typeof UserAction>;

// Labels and colors
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  'admin': 'Administrator',
  'finance': 'Finance',
  'buyer': 'Buyer',
  'supervisor': 'Supervisor',
  'site-manager': 'Site Manager',
  'storekeeper': 'Storekeeper',
  'receiving': 'Receiving',
  'chef': 'Chef',
  'venue-staff': 'Venue Staff',
  'supplier-admin': 'Supplier Admin',
  'supplier-sales': 'Supplier Sales',
  'supplier-delivery': 'Supplier Delivery',
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  'active': 'Active',
  'pending': 'Pending Invite',
  'disabled': 'Disabled',
  'unconfirmed': 'Unconfirmed',
};

export const USER_STATUS_COLORS: Record<UserStatus, string> = {
  'active': 'bg-green-100 text-green-800',
  'pending': 'bg-amber-100 text-amber-800',
  'disabled': 'bg-gray-100 text-gray-800',
  'unconfirmed': 'bg-blue-100 text-blue-800',
};

// Internal vs Supplier roles
export const INTERNAL_ROLES: UserRole[] = [
  'admin', 'finance', 'buyer', 'supervisor', 
  'site-manager', 'storekeeper', 'receiving', 'chef', 'venue-staff'
];

export const SUPPLIER_ROLES: UserRole[] = [
  'supplier-admin', 'supplier-sales', 'supplier-delivery'
];

export function isSupplierRole(role: UserRole): boolean {
  return SUPPLIER_ROLES.includes(role);
}
